/*
  <free-gift-modal>
  --------------------------------------------------------------------------
  Powers the free-gift modal (snippets/cart-free-gift.liquid + the per-list card
  in snippets/cart-free-gift-card.liquid).

  The modal, cards and product panels are rendered server-side (hidden until opened).
  The modal NEVER opens on its own — it opens only from the cart-drawer button. This
  script:
   - reveals the cart-drawer trigger button ([data-fgm-open]) once the qualifying
     total reaches the threshold, and opens the modal when that button is clicked,
   - switches a card's visible product when its dropdown changes,
   - closes it (overlay / X / Esc),
   - adds the chosen gift (hidden line-item property `_free_gift`) and refreshes
     the drawer.

  Qualifying total counts only cart products carrying cart_gift_tag (read from
  /products/{handle}.js, cached). Blank tag -> every product counts.

  "Already have a gift" isn't limited to lines carrying `_free_gift` — a gift-list
  product bought normally from a collection/product page counts too (it'll be
  covered by the same Shopify automatic discount), so the trigger stays hidden
  once any configured gift product sits in the cart, however it got there.

  Force-reveal the button for testing: theme setting "Test mode" or the ?freegift
  URL param (the modal still only opens on click).
*/
if (!customElements.get('free-gift-modal')) {
  const GIFT_PROPERTY = '_free_gift';
  const formatMoney = (cents) => '€' + (cents / 100).toFixed(2).replace('.', ',');

  class FreeGiftModal extends HTMLElement {
    constructor() {
      super();
      this.threshold = parseInt(this.dataset.threshold, 10) || 0;
      this.tag = (this.dataset.tag || '').trim().toLowerCase();
      this.progressLabel = this.dataset.progressLabel || '';
      this.progressLabelComplete = this.dataset.progressLabelComplete || '';
      this.tagCache = new Map(); // handle -> tags[]
      // Every product offered as a gift, across both cards — used to detect a
      // gift-list product that reached the cart through a normal add-to-cart.
      this.giftProductIds = new Set(
        Array.from(this.querySelectorAll('.fgm-product')).map((panel) => panel.dataset.productId)
      );

      const testParam = new URLSearchParams(window.location.search).has('freegift');
      this.forcePreview = testParam || this.dataset.testMode === 'true';
      if (this.forcePreview) window.freeGiftDebug = true;

      this.busy = false;
      this.isOpen = false; // modal only ever opens from the cart-drawer button

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('change', this.onChange.bind(this));
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) this.close();
      });

      this.bindTriggers();

      const start = () => this.evaluate();
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start);
      } else {
        start();
      }
    }

    bindTriggers() {
      document.querySelectorAll('.cartToggler').forEach((opener) => {
        opener.addEventListener('click', () => setTimeout(() => this.evaluate(), 300));
      });

      // The cart-drawer header button opens the modal once it's been revealed.
      document.querySelectorAll('[data-fgm-open]').forEach((button) => {
        button.addEventListener('click', () => this.open());
      });

      const totals = document.querySelector('.totals__subtotal_value');
      if (totals) {
        let timer;
        const observer = new MutationObserver(() => {
          clearTimeout(timer);
          timer = setTimeout(() => this.evaluate(), 250);
        });
        observer.observe(totals, { childList: true, characterData: true, subtree: true });
      }
    }

    // Show or hide the cart-drawer trigger button.
    showTrigger(show) {
      document.querySelectorAll('[data-fgm-open]').forEach((button) => {
        button.classList.toggle('none', !show);
      });
    }

    // Fill the header progress bar toward the threshold. The bar stays visible
    // the whole time there is a cart with a threshold: while filling it shows the
    // remaining amount, and once full it switches to the "unlocked" copy while the
    // trigger button appears beneath it. Hidden only when the cart is empty (or a
    // gift is already chosen) or when there is no threshold to work toward.
    updateProgress(qualifying, hasItems) {
      const wrap = document.querySelector('[data-fgm-progress]');
      if (!wrap) return;

      const active = hasItems && this.threshold > 0;
      if (active) {
        const eligible = qualifying >= this.threshold;
        const pct = Math.max(0, Math.min(100, (qualifying / this.threshold) * 100));
        const fill = wrap.querySelector('[data-fgm-progress-fill]');
        const message = wrap.querySelector('[data-fgm-progress-msg]');
        if (fill) fill.style.width = pct.toFixed(1) + '%';
        if (message) {
          const remaining = Math.max(0, this.threshold - qualifying);
          message.textContent = eligible
            ? this.progressCompleteMessage()
            : this.progressMessage(remaining);
        }
        wrap.classList.toggle('is-complete', eligible);
      }
      wrap.classList.toggle('none', !active);
      this.syncHeaderSpacer();
    }

    progressMessage(remainingCents) {
      const template = this.progressLabel || 'Πρόσθεσε ακόμα [amount]';
      return template.replace(/\[amount\]/g, formatMoney(remainingCents));
    }

    progressCompleteMessage() {
      const template = this.progressLabelComplete || 'Κέρδισες το δώρο σου!';
      return template.replace(/\[amount\]/g, formatMoney(this.threshold));
    }

    // The drawer header is position:fixed, so the scrollable content is offset by
    // a fixed spacer. Keep that spacer matched to the header's real height as the
    // progress bar / trigger appear and disappear.
    syncHeaderSpacer() {
      const header = document.querySelector('[data-cart-header]');
      const wrapper = document.querySelector('.cartFormWrapper');
      if (!header || !wrapper) return;
      const height = header.offsetHeight;
      if (height) wrapper.style.marginTop = height + 'px';
    }

    /* -------------------------------------------------- card interactions */

    // Switch the visible product panel when a card's product dropdown changes.
    onChange(event) {
      const productSelect = event.target.closest('.fgm-product-select');
      if (!productSelect) return;
      const card = productSelect.closest('[data-fgm-card]');
      if (!card) return;
      const selectedId = productSelect.value;
      card.querySelectorAll('.fgm-product').forEach((panel) => {
        panel.hidden = panel.dataset.productId !== selectedId;
      });
    }

    /* ---------------------------------------------------------- evaluate */

    async evaluate() {
      const debug = window.freeGiftDebug === true;
      if (this.busy) return;
      this.busy = true;

      try {
        if (typeof CartFunctions === 'undefined') {
          // Can't read the cart to check for an existing gift — fall back to
          // just force-revealing the trigger so preview/test mode still works.
          if (this.forcePreview) {
            this.showTrigger(true);
            this.updateProgress(this.threshold, true);
          }
          return;
        }

        const cart = await new CartFunctions().getCart();
        const items = cart.items || [];

        // A gift already in the cart always wins — even in preview/test mode —
        // so the trigger/progress bar never linger once the reward is claimed,
        // whether it was chosen via the modal or just bought normally.
        if (items.some((item) => this.isGift(item) || this.giftProductIds.has(String(item.product_id)))) {
          this.showTrigger(false);
          this.updateProgress(0, false);
          return;
        }

        if (this.forcePreview) {
          this.showTrigger(true);
          this.updateProgress(this.threshold, true);
          return;
        }

        const qualifying = await this.qualifyingTotal(items);
        const eligible = qualifying >= this.threshold;
        if (debug) {
          console.log(`[free-gift] tag="${this.tag}" qualifying=${formatMoney(qualifying)} ` +
            `threshold=${formatMoney(this.threshold)} -> ${eligible ? 'eligible' : 'not yet'}`);
        }
        this.showTrigger(eligible);
        this.updateProgress(qualifying, items.length > 0);
      } catch (error) {
        console.error('[free-gift] evaluate failed', error);
      } finally {
        this.busy = false;
      }
    }

    isGift(item) {
      const props = item.properties || {};
      return props[GIFT_PROPERTY] != null && props[GIFT_PROPERTY] !== '';
    }

    // Sum of non-gift line totals, restricted to products carrying the tag.
    async qualifyingTotal(items) {
      const payable = items.filter((item) => !this.isGift(item));
      if (!this.tag) {
        return payable.reduce((sum, item) => sum + item.final_line_price, 0);
      }
      let total = 0;
      for (const item of payable) {
        const tags = await this.fetchTags(item.handle);
        if (tags.some((t) => String(t).trim().toLowerCase() === this.tag)) {
          total += item.final_line_price;
        }
      }
      return total;
    }

    async fetchTags(handle) {
      if (!handle) return [];
      if (this.tagCache.has(handle)) return this.tagCache.get(handle);
      try {
        const response = await fetch(`/products/${handle}.js`);
        const tags = response.ok ? (await response.json()).tags || [] : [];
        this.tagCache.set(handle, tags);
        return tags;
      } catch (error) {
        console.error('[free-gift] tag fetch failed for', handle, error);
        this.tagCache.set(handle, []);
        return [];
      }
    }

    /* ------------------------------------------------------- open / close */

    open() {
      if (this.isOpen) return;
      this.classList.remove('none');
      this.isOpen = true;
      document.body.style.overflow = 'hidden';
    }

    close() {
      this.classList.add('none');
      this.isOpen = false;
      document.body.style.overflow = '';
    }

    /* ----------------------------------------------------------- choose */

    onClick(event) {
      if (event.target.closest('[data-fgm-close]')) {
        event.preventDefault();
        this.close();
        return;
      }
      const chooseButton = event.target.closest('[data-fgm-choose]');
      if (chooseButton) {
        event.preventDefault();
        this.chooseGift(chooseButton);
      }
    }

    async chooseGift(button) {
      // Only one gift may be added at a time — block concurrent (fast) clicks
      // across both cards.
      if (this.adding) return;

      const card = button.closest('[data-fgm-card]');
      // The visible product panel decides which variant gets added.
      const panel = card && (card.querySelector('.fgm-product:not([hidden])') || card.querySelector('.fgm-product'));
      const select = panel && panel.querySelector('.fgm-variant');
      const variantId = select ? select.value : panel && panel.dataset.defaultVariant;
      const productId = panel && panel.dataset.productId;
      if (!variantId) return;
      if (typeof CartFunctions === 'undefined') {
        console.warn('[free-gift] CartFunctions not available');
        return;
      }

      this.adding = true;
      const cartFunctions = new CartFunctions();
      button.classList.add('is-loading');

      try {
        const cart = await cartFunctions.getCart();
        const items = cart.items || [];
        // The gift discount is matched by product, so a paid line of the same
        // product already in the cart would get discounted too, handing the
        // customer a second free unit. Block that instead of adding the gift.
        const alreadyOwned = items.some(
          (item) => !this.isGift(item) && String(item.product_id) === String(productId)
        );
        if (alreadyOwned) {
          this.showCardWarning(card);
          return;
        }

        // Drop any gift already in the cart so only this one remains.
        await this.removeExistingGifts(cartFunctions);
        await cartFunctions.addItemWithProperties(variantId, 1, { [GIFT_PROPERTY]: 'true' });
        await this.refreshDrawer(cartFunctions);
        // Hide the trigger/progress bar directly: the gift line nets to €0 (100%
        // off), so the subtotal text often doesn't change and the mutation
        // observer that normally drives evaluate() never fires.
        this.showTrigger(false);
        this.updateProgress(0, false);
        this.close();
      } catch (error) {
        console.error('[free-gift] choose gift failed', error);
      } finally {
        this.adding = false;
        button.classList.remove('is-loading');
      }
    }

    showCardWarning(card) {
      const warning = card && card.querySelector('[data-fgm-warning]');
      if (!warning) return;
      warning.hidden = false;
      clearTimeout(this.warningTimer);
      this.warningTimer = setTimeout(() => { warning.hidden = true; }, 4000);
    }

    // Remove every gift line (by line index, bottom-up so indexes stay valid).
    async removeExistingGifts(cartFunctions) {
      const cart = await cartFunctions.getCart();
      const items = cart.items || [];
      const lines = items
        .map((item, index) => (this.isGift(item) ? index + 1 : 0))
        .filter((line) => line > 0)
        .sort((a, b) => b - a);
      for (const line of lines) {
        await cartFunctions.removeItemByLine(line);
      }
    }

    /* -------------------------------------------------- drawer refresh */

    // Delegate to the cart's own repaint so the drawer is rebuilt exactly one way
    // (line indexes, split BOGO lines, discount copy, totals and empty state).
    async refreshDrawer(cartFunctions) {
      const ajaxCart = document.querySelector('ajax-cart');
      if (!ajaxCart || typeof ajaxCart.renderCart !== 'function') return;
      ajaxCart.renderCart(await cartFunctions.getCart());
    }
  }

  customElements.define('free-gift-modal', FreeGiftModal);
}
